import { useState, useRef, useCallback, useEffect } from "react";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Tag, Type, Barcode, QrCode, Move } from "lucide-react";

// Campos disponíveis para usar nas etiquetas
const CAMPOS_DISPONIVEIS = [
  { value: "cod", label: "Código do Produto" },
  { value: "descricao", label: "Descrição" },
  { value: "ncm", label: "NCM" },
  { value: "eanUnit", label: "EAN Unitário" },
  { value: "eanCaixa", label: "EAN Caixa" },
  { value: "unidadeFiscal", label: "Unidade Fiscal" },
  { value: "unidadeComercial", label: "Unidade Comercial" },
  { value: "qtdCaixaMaster", label: "Qtd Caixa Master" },
  { value: "altura", label: "Altura (cm)" },
  { value: "largura", label: "Largura (cm)" },
  { value: "comprimento", label: "Comprimento (cm)" },
  { value: "custo", label: "Custo" },
  { value: "venda", label: "Venda" },
];

interface LabelElemento {
  id: string;
  tipo: "texto" | "barcode" | "qrcode";
  campo: string;
  x: number;
  y: number;
  largura: number;
  altura: number;
  fontSize?: number;
}

interface LabelTemplate {
  id: number;
  nome: string;
  larguraMm: number;
  alturaMm: number;
  elementos: LabelElemento[];
  criadoEm: string;
  atualizadoEm: string;
}

// Escala de visualização: pixels por mm
const PX_PER_MM = 3.5;

function gerarId(): string {
  return Math.random().toString(36).substring(2, 10);
}

function ElementoPreview({ el, campo, selecionado, onSelect, onDragEnd }: {
  el: LabelElemento;
  campo: string;
  selecionado: boolean;
  onSelect: () => void;
  onDragEnd: (x: number, y: number) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const startPos = useRef({ mouseX: 0, mouseY: 0, elX: 0, elY: 0 });

  function handleMouseDown(e: React.MouseEvent) {
    e.stopPropagation();
    onSelect();
    dragging.current = true;
    startPos.current = { mouseX: e.clientX, mouseY: e.clientY, elX: el.x, elY: el.y };

    function onMove(ev: MouseEvent) {
      if (!dragging.current) return;
      const dx = (ev.clientX - startPos.current.mouseX) / PX_PER_MM;
      const dy = (ev.clientY - startPos.current.mouseY) / PX_PER_MM;
      const newX = Math.max(0, startPos.current.elX + dx);
      const newY = Math.max(0, startPos.current.elY + dy);
      if (ref.current) {
        ref.current.style.left = `${newX * PX_PER_MM}px`;
        ref.current.style.top = `${newY * PX_PER_MM}px`;
      }
    }

    function onUp(ev: MouseEvent) {
      if (!dragging.current) return;
      dragging.current = false;
      const dx = (ev.clientX - startPos.current.mouseX) / PX_PER_MM;
      const dy = (ev.clientY - startPos.current.mouseY) / PX_PER_MM;
      const newX = Math.max(0, Math.round(startPos.current.elX + dx));
      const newY = Math.max(0, Math.round(startPos.current.elY + dy));
      onDragEnd(newX, newY);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    }

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }

  const iconeTipo = el.tipo === "barcode" ? "▌▌▌▌▌" : el.tipo === "qrcode" ? "▦" : campo;

  return (
    <div
      ref={ref}
      onMouseDown={handleMouseDown}
      style={{
        position: "absolute",
        left: el.x * PX_PER_MM,
        top: el.y * PX_PER_MM,
        width: el.largura * PX_PER_MM,
        height: el.altura * PX_PER_MM,
        border: selecionado ? "2px solid #3b82f6" : "1px dashed #94a3b8",
        background: selecionado ? "rgba(59,130,246,0.08)" : "rgba(255,255,255,0.7)",
        cursor: "move",
        userSelect: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        borderRadius: 2,
        zIndex: selecionado ? 10 : 1,
      }}
    >
      <span style={{ fontSize: Math.min((el.fontSize || 10) * PX_PER_MM * 0.35, el.altura * PX_PER_MM * 0.8), color: "#334155", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", padding: "0 2px" }}>
        {iconeTipo}
      </span>
    </div>
  );
}

function EditorCanvas({ larguraMm, alturaMm, elementos, selecionadoId, onSelect, onUpdatePos }: {
  larguraMm: number;
  alturaMm: number;
  elementos: LabelElemento[];
  selecionadoId: string | null;
  onSelect: (id: string | null) => void;
  onUpdatePos: (id: string, x: number, y: number) => void;
}) {
  return (
    <div
      style={{
        position: "relative",
        width: larguraMm * PX_PER_MM,
        height: alturaMm * PX_PER_MM,
        background: "white",
        border: "2px solid #64748b",
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        overflow: "hidden",
        flexShrink: 0,
      }}
      onClick={() => onSelect(null)}
    >
      {/* Grid de fundo */}
      <svg style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", pointerEvents: "none" }}>
        <defs>
          <pattern id="grid" width={5 * PX_PER_MM} height={5 * PX_PER_MM} patternUnits="userSpaceOnUse">
            <path d={`M ${5 * PX_PER_MM} 0 L 0 0 0 ${5 * PX_PER_MM}`} fill="none" stroke="#e2e8f0" strokeWidth="0.5" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid)" />
      </svg>

      {elementos.map((el) => {
        const campoLabel = CAMPOS_DISPONIVEIS.find(c => c.value === el.campo)?.label ?? el.campo;
        return (
          <ElementoPreview
            key={el.id}
            el={el}
            campo={campoLabel}
            selecionado={selecionadoId === el.id}
            onSelect={() => onSelect(el.id)}
            onDragEnd={(x, y) => onUpdatePos(el.id, x, y)}
          />
        );
      })}
    </div>
  );
}

export default function TemplateEtiquetas() {
  const { data: templates = [], refetch, isLoading } = trpc.labelTemplates.list.useQuery();

  // Estado do modal de lista (criar/editar template)
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  // Campos do template
  const [nome, setNome] = useState("");
  const [larguraMm, setLarguraMm] = useState(100);
  const [alturaMm, setAlturaMm] = useState(50);
  const [elementos, setElementos] = useState<LabelElemento[]>([]);

  // Elemento selecionado no editor
  const [selecionadoId, setSelecionadoId] = useState<string | null>(null);

  // Painel de adicionar elemento
  const [novoTipo, setNovoTipo] = useState<"texto" | "barcode" | "qrcode">("texto");
  const [novoCampo, setNovoCampo] = useState("cod");

  // Modal de confirmação de exclusão
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  const createMutation = trpc.labelTemplates.create.useMutation({
    onSuccess: () => {
      toast.success("Template criado com sucesso.");
      refetch();
      setModalOpen(false);
    },
    onError: (err) => toast.error("Erro ao criar template: " + err.message),
  });

  const updateMutation = trpc.labelTemplates.update.useMutation({
    onSuccess: () => {
      toast.success("Template atualizado com sucesso.");
      refetch();
      setModalOpen(false);
    },
    onError: (err) => toast.error("Erro ao atualizar template: " + err.message),
  });

  const deleteMutation = trpc.labelTemplates.delete.useMutation({
    onSuccess: () => {
      toast.success("Template excluído com sucesso.");
      refetch();
      setDeleteConfirmId(null);
    },
    onError: (err) => toast.error("Erro ao excluir template: " + err.message),
  });

  function openNew() {
    setEditingId(null);
    setNome("");
    setLarguraMm(100);
    setAlturaMm(50);
    setElementos([]);
    setSelecionadoId(null);
    setModalOpen(true);
  }

  function openEdit(t: LabelTemplate) {
    setEditingId(t.id);
    setNome(t.nome);
    setLarguraMm(t.larguraMm);
    setAlturaMm(t.alturaMm);
    setElementos(t.elementos ? [...t.elementos] : []);
    setSelecionadoId(null);
    setModalOpen(true);
  }

  function handleSave() {
    if (!nome.trim()) {
      toast.error("Informe o nome do template.");
      return;
    }
    const payload = { nome: nome.trim(), larguraMm, alturaMm, elementos };
    if (editingId !== null) {
      updateMutation.mutate({ id: editingId, ...payload });
    } else {
      createMutation.mutate(payload);
    }
  }

  function adicionarElemento() {
    const novo: LabelElemento = {
      id: gerarId(),
      tipo: novoTipo,
      campo: novoCampo,
      x: 5,
      y: 5,
      largura: novoTipo === "qrcode" ? 20 : 40,
      altura: novoTipo === "qrcode" ? 20 : (novoTipo === "barcode" ? 15 : 8),
      fontSize: novoTipo === "texto" ? 10 : undefined,
    };
    setElementos(prev => [...prev, novo]);
    setSelecionadoId(novo.id);
  }

  function removerElementoSelecionado() {
    if (!selecionadoId) return;
    setElementos(prev => prev.filter(e => e.id !== selecionadoId));
    setSelecionadoId(null);
  }

  function updatePos(id: string, x: number, y: number) {
    setElementos(prev => prev.map(e => e.id === id ? { ...e, x, y } : e));
  }

  function updateElementoProp(id: string, prop: keyof LabelElemento, value: any) {
    setElementos(prev => prev.map(e => e.id === id ? { ...e, [prop]: value } : e));
  }

  const selecionado = elementos.find(e => e.id === selecionadoId) ?? null;

  function formatDate(dateStr: string) {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toLocaleDateString("pt-BR") + " " + d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Tag className="w-6 h-6" />
            Template de Etiquetas
          </h1>
          <p className="text-muted-foreground text-sm">Crie e gerencie templates de etiquetas para impressão</p>
        </div>
        <Button onClick={openNew}>
          <Plus className="w-4 h-4 mr-2" />
          Novo Template
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Templates Cadastrados</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Carregando...</p>
          ) : templates.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum template cadastrado.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3 font-medium">Nome</th>
                    <th className="text-left py-2 px-3 font-medium">Tamanho</th>
                    <th className="text-left py-2 px-3 font-medium">Elementos</th>
                    <th className="text-left py-2 px-3 font-medium">Criado em</th>
                    <th className="text-right py-2 px-3 font-medium">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {templates.map((t) => (
                    <tr key={t.id} className="border-b hover:bg-muted/50">
                      <td className="py-2 px-3 font-medium">{t.nome}</td>
                      <td className="py-2 px-3 text-muted-foreground">{t.larguraMm} × {t.alturaMm} mm</td>
                      <td className="py-2 px-3">
                        <div className="flex flex-wrap gap-1">
                          {t.elementos.map((el) => (
                            <span key={el.id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-700 text-xs rounded">
                              {el.tipo === "barcode" ? <Barcode className="w-3 h-3" /> : el.tipo === "qrcode" ? <QrCode className="w-3 h-3" /> : <Type className="w-3 h-3" />}
                              {CAMPOS_DISPONIVEIS.find(c => c.value === el.campo)?.label ?? el.campo}
                            </span>
                          ))}
                          {t.elementos.length === 0 && <span className="text-muted-foreground text-xs">Sem elementos</span>}
                        </div>
                      </td>
                      <td className="py-2 px-3 text-muted-foreground text-xs">{formatDate(t.criadoEm)}</td>
                      <td className="py-2 px-3 text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(t)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setDeleteConfirmId(t.id)}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal Editor de Template */}
      <Dialog open={modalOpen} onOpenChange={(open) => { if (!open) setModalOpen(false); }}>
        <DialogContent className="!w-[80vw] !h-[80vh] !max-w-none overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar Template" : "Novo Template"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Configurações gerais */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label>Nome do Template</Label>
                <Input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Ex: Etiqueta Padrão 100x50" />
              </div>
              <div className="space-y-1">
                <Label>Largura (mm)</Label>
                <Input
                  type="number"
                  value={larguraMm}
                  onChange={(e) => setLarguraMm(Number(e.target.value))}
                  min={20}
                  max={300}
                />
              </div>
              <div className="space-y-1">
                <Label>Altura (mm)</Label>
                <Input
                  type="number"
                  value={alturaMm}
                  onChange={(e) => setAlturaMm(Number(e.target.value))}
                  min={10}
                  max={300}
                />
              </div>
            </div>

            {/* Área principal: canvas + painel lateral */}
            <div className="flex gap-4">
              {/* Canvas de edição */}
              <div className="flex-1 overflow-auto">
                <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                  <Move className="w-3 h-3" />
                  Arraste os elementos para posicioná-los na etiqueta
                </p>
                <div className="overflow-auto border rounded bg-slate-100 p-4">
                  <EditorCanvas
                    larguraMm={larguraMm}
                    alturaMm={alturaMm}
                    elementos={elementos}
                    selecionadoId={selecionadoId}
                    onSelect={setSelecionadoId}
                    onUpdatePos={updatePos}
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Tamanho: {larguraMm} × {alturaMm} mm &nbsp;|&nbsp; Escala: 1mm = {PX_PER_MM}px
                </p>
              </div>

              {/* Painel lateral */}
              <div className="w-72 space-y-4 flex-shrink-0">
                {/* Adicionar elemento */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Adicionar Elemento</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Tipo</Label>
                      <Select value={novoTipo} onValueChange={(v) => setNovoTipo(v as any)}>
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="texto">
                            <span className="flex items-center gap-2"><Type className="w-3 h-3" /> Texto</span>
                          </SelectItem>
                          <SelectItem value="barcode">
                            <span className="flex items-center gap-2"><Barcode className="w-3 h-3" /> Código de Barras</span>
                          </SelectItem>
                          <SelectItem value="qrcode">
                            <span className="flex items-center gap-2"><QrCode className="w-3 h-3" /> QR Code</span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Campo</Label>
                      <Select value={novoCampo} onValueChange={setNovoCampo}>
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CAMPOS_DISPONIVEIS.map(c => (
                            <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button size="sm" className="w-full" onClick={adicionarElemento}>
                      <Plus className="w-3 h-3 mr-1" />
                      Adicionar
                    </Button>
                  </CardContent>
                </Card>

                {/* Propriedades do elemento selecionado */}
                {selecionado && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center justify-between">
                        <span>Propriedades</span>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={removerElementoSelecionado}>
                          <Trash2 className="w-3 h-3 text-destructive" />
                        </Button>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label className="text-xs">X (mm)</Label>
                          <Input
                            type="number"
                            className="h-7 text-xs"
                            value={selecionado.x}
                            onChange={(e) => updateElementoProp(selecionado.id, "x", Number(e.target.value))}
                            min={0}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Y (mm)</Label>
                          <Input
                            type="number"
                            className="h-7 text-xs"
                            value={selecionado.y}
                            onChange={(e) => updateElementoProp(selecionado.id, "y", Number(e.target.value))}
                            min={0}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Largura (mm)</Label>
                          <Input
                            type="number"
                            className="h-7 text-xs"
                            value={selecionado.largura}
                            onChange={(e) => updateElementoProp(selecionado.id, "largura", Number(e.target.value))}
                            min={5}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Altura (mm)</Label>
                          <Input
                            type="number"
                            className="h-7 text-xs"
                            value={selecionado.altura}
                            onChange={(e) => updateElementoProp(selecionado.id, "altura", Number(e.target.value))}
                            min={3}
                          />
                        </div>
                      </div>
                      {selecionado.tipo === "texto" && (
                        <div className="space-y-1">
                          <Label className="text-xs">Tamanho da Fonte (pt)</Label>
                          <Input
                            type="number"
                            className="h-7 text-xs"
                            value={selecionado.fontSize ?? 10}
                            onChange={(e) => updateElementoProp(selecionado.id, "fontSize", Number(e.target.value))}
                            min={6}
                            max={72}
                          />
                        </div>
                      )}
                      <div className="space-y-1">
                        <Label className="text-xs">Campo</Label>
                        <Select value={selecionado.campo} onValueChange={(v) => updateElementoProp(selecionado.id, "campo", v)}>
                          <SelectTrigger className="h-7 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {CAMPOS_DISPONIVEIS.map(c => (
                              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Tipo</Label>
                        <Select value={selecionado.tipo} onValueChange={(v) => updateElementoProp(selecionado.id, "tipo", v)}>
                          <SelectTrigger className="h-7 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="texto">Texto</SelectItem>
                            <SelectItem value="barcode">Código de Barras</SelectItem>
                            <SelectItem value="qrcode">QR Code</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Lista de elementos */}
                {elementos.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Elementos ({elementos.length})</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-1">
                      {elementos.map((el) => {
                        const campoLabel = CAMPOS_DISPONIVEIS.find(c => c.value === el.campo)?.label ?? el.campo;
                        return (
                          <div
                            key={el.id}
                            className={`flex items-center gap-2 p-1.5 rounded cursor-pointer text-xs ${selecionadoId === el.id ? "bg-blue-50 border border-blue-200" : "hover:bg-muted"}`}
                            onClick={() => setSelecionadoId(el.id)}
                          >
                            {el.tipo === "barcode" ? <Barcode className="w-3 h-3 flex-shrink-0" /> : el.tipo === "qrcode" ? <QrCode className="w-3 h-3 flex-shrink-0" /> : <Type className="w-3 h-3 flex-shrink-0" />}
                            <span className="truncate">{campoLabel}</span>
                            <span className="ml-auto text-muted-foreground flex-shrink-0">{el.x},{el.y}</span>
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={createMutation.isPending || updateMutation.isPending}>
              {editingId ? "Salvar Alterações" : "Criar Template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de confirmação de exclusão */}
      <Dialog open={deleteConfirmId !== null} onOpenChange={(open) => { if (!open) setDeleteConfirmId(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Excluir Template</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">Tem certeza que deseja excluir este template? Esta ação não pode ser desfeita.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={() => deleteConfirmId !== null && deleteMutation.mutate({ id: deleteConfirmId })} disabled={deleteMutation.isPending}>
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
